import React, { useState } from 'react';
import NoteInput from './NoteInput';
import NoteCard from './NoteCard';
import useMeetingNotes from '../../hooks/useMeetingNotes';

// Inline SVG icons
const DocumentTextIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const GlobeAltIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

const LockClosedIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const QuestionMarkIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Hjälp-tooltip komponent för röstkommandon
const VoiceCommandsHelp = () => (
  <div className="relative group inline-block">
    <button
      type="button"
      className="text-gray-400 hover:text-gray-600 focus:outline-none"
      title="Röstkommandon"
    >
      <QuestionMarkIcon className="w-4 h-4" />
    </button>
    <div className="absolute left-0 top-6 z-50 hidden group-hover:block w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg">
      <div className="font-semibold mb-2">Röstkommandon:</div>
      <table className="w-full">
        <tbody>
          <tr><td className="pr-2 py-0.5 text-gray-300">"stopp"</td><td>→ avslutar inspelningen</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"punkt"</td><td>→ .</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"komma"</td><td>→ ,</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"bindestreck"</td><td>→ -</td></tr>
          <tr><td className="pr-2 py-0.5 text-gray-300">"radbryt"</td><td>→ ny rad</td></tr>
        </tbody>
      </table>
      {/* Pil */}
      <div className="absolute -top-1 left-2 w-2 h-2 bg-gray-900 transform rotate-45"></div>
    </div>
  </div>
);

/**
 * Sektion för mötesanteckningar
 *
 * @param {Object} props
 * @param {string} props.meetingId - ID för mötet
 * @param {string} props.meetingTitle - Mötets titel (för rubrik)
 * @param {boolean} props.showHeader - Visa sektion-header
 * @param {boolean} props.compact - Kompakt visning
 */
export function MeetingNotesSection({
  meetingId,
  meetingTitle,
  showHeader = true,
  compact = false
}) {
  const [filter, setFilter] = useState('all'); // 'all', 'shared', 'internal'

  const {
    notes,
    counts,
    loading,
    error,
    createNote,
    updateNote,
    deleteNote,
    updateVisibility,
    updateActionPoint
  } = useMeetingNotes(meetingId);

  // Filtrera anteckningar
  const filteredNotes = notes.filter(note => {
    if (filter === 'all') return true;
    return note.visibility === filter;
  });

  // Hantera skapa anteckning
  const handleCreateNote = async (noteData) => {
    await createNote(noteData);
  };

  if (!meetingId) {
    return (
      <div className="p-4 text-center text-gray-500">
        Välj ett möte för att se anteckningar
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full ${compact ? '' : 'bg-white rounded-lg border'}`}>
      {/* Header */}
      {showHeader && (
        <div className="px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-500" />
              <h3 className="font-medium text-gray-900">
                Anteckningar
                {meetingTitle && <span className="text-gray-500 ml-1">- {meetingTitle}</span>}
              </h3>
              {counts.total > 0 && (
                <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {counts.total}
                </span>
              )}
              <VoiceCommandsHelp />
            </div>

            {/* Filter */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setFilter('all')}
                className={`px-2 py-1 text-xs rounded transition-colors
                  ${filter === 'all'
                    ? 'bg-gray-200 text-gray-800'
                    : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                Alla ({counts.total})
              </button>
              <button
                onClick={() => setFilter('shared')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
                  ${filter === 'shared'
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                <GlobeAltIcon className="w-3.5 h-3.5" />
                Delade ({counts.shared})
              </button>
              <button
                onClick={() => setFilter('internal')}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors
                  ${filter === 'internal'
                    ? 'bg-gray-200 text-gray-700'
                    : 'text-gray-500 hover:bg-gray-100'
                  }`}
              >
                <LockClosedIcon className="w-3.5 h-3.5" />
                Mina ({counts.internal})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes list */}
      <div className={`flex-1 overflow-y-auto ${compact ? 'p-2' : 'p-4'}`}>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-leadme-pink border-t-transparent" />
          </div>
        ) : error ? (
          <div className="text-center py-4 text-red-600">{error}</div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {filter === 'all' ? (
              <>
                <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>Inga anteckningar ännu</p>
                <p className="text-sm mt-1">Skriv eller diktera en anteckning nedan</p>
              </>
            ) : filter === 'shared' ? (
              'Inga delade anteckningar'
            ) : (
              'Inga privata anteckningar'
            )}
          </div>
        ) : (
          <div className={`space-y-${compact ? '2' : '3'}`}>
            {filteredNotes.map(note => (
              <NoteCard
                key={note._id}
                note={note}
                onUpdate={updateNote}
                onDelete={deleteNote}
                onToggleVisibility={updateVisibility}
                onToggleActionPoint={updateActionPoint}
                compact={compact}
              />
            ))}
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`border-t ${compact ? 'p-2' : 'p-4'}`}>
        <NoteInput
          onSubmit={handleCreateNote}
          disabled={loading}
          placeholder="Skriv eller diktera en anteckning..."
        />
      </div>
    </div>
  );
}

export default MeetingNotesSection;
